import React, { useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { type BeamerUpdate, type CmsContentItem, getLoginContent } from '../api/loginApi';

import { PlatformUpdates } from './PlatformUpdates';

const ROTATE_INTERVAL_MS = 7000;
const FADE_DURATION_MS = 400;

export const VideoContentOverlay = () => {
    const intl = useIntl();
    const [items, setItems] = useState<CmsContentItem[]>([]);
    const [updates, setUpdates] = useState<BeamerUpdate[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isFading, setIsFading] = useState(false);

    useEffect(() => {
        getLoginContent()
            .then((data) => {
                setItems(data.content);
                setUpdates(data.updates);
            })
            .catch(() => {
                // silently ignore — overlay just won't show
            });
    }, []);

    const goTo = useCallback(
        (index: number) => {
            if (index === activeIndex) return;
            setIsFading(true);
            setTimeout(() => {
                setActiveIndex(index);
                setIsFading(false);
            }, FADE_DURATION_MS);
        },
        [activeIndex],
    );

    // Auto-rotate
    useEffect(() => {
        if (items.length <= 1) return;
        const timer = setInterval(() => {
            goTo((activeIndex + 1) % items.length);
        }, ROTATE_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [items.length, activeIndex, goTo]);

    if (items.length === 0 && updates.length === 0) return null;

    const item = items.length > 0 ? items[activeIndex] : null;
    const categoryLabel = item
        ? item.category.charAt(0).toUpperCase() + item.category.slice(1)
        : '';

    return (
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-rounded-b-large tw-overflow-hidden tw-pointer-events-auto">
            <div className="tw-px-24 tw-pt-48 tw-pb-24">
                <div
                    className="tw-flex tw-gap-16 tw-items-center tw-transition-opacity tw-ease-in-out"
                    style={{
                        opacity: isFading ? 0 : 1,
                        transitionDuration: `${FADE_DURATION_MS}ms`,
                    }}
                >
                    {/* Image */}
                    {item?.image && (
                        <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tw-shrink-0"
                        >
                            <img
                                src={item.image}
                                alt={item.title}
                                className="tw-h-[140px] tw-object-cover tw-rounded-large"
                                style={{
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                }}
                            />
                        </a>
                    )}

                    {item && (
                        <div className="tw-min-w-0">
                            {/* Category pill */}
                            <span className="tw-inline-block tw-text-s tw-font-medium tw-tracking-xx-loose tw-uppercase tw-text-white/90 tw-border tw-border-white/30 tw-rounded tw-px-8 tw-py-2">
                                {categoryLabel}
                            </span>

                            {/* Title */}
                            <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tw-block tw-text-white hover:tw-text-white tw-no-underline hover:tw-underline"
                            >
                                <h3 className="tw-text-l tw-font-semibold tw-leading-tight tw-mb-4 tw-line-clamp-2">
                                    {item.title}
                                </h3>
                            </a>

                            {/* Paragraph */}
                            <p className="tw-text-s tw-text-white/70 tw-leading-snug tw-mb-8 tw-line-clamp-2">
                                {item.paragraph}
                            </p>

                            {/* Link */}
                            <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tw-text-s tw-font-medium tw-text-[#86d9d6] hover:tw-text-[#86d9d6] hover:tw-opacity-80 tw-no-underline tw-inline-flex tw-items-center tw-gap-4"
                            >
                                {item.linkText || (
                                    <FormattedMessage
                                        id="login.content.readMore"
                                        defaultMessage="Read more"
                                    />
                                )}
                                <span className="tw-text-s">→</span>
                            </a>
                        </div>
                    )}
                </div>

                {/* Dot indicators */}
                {items.length > 1 && (
                    <div className="tw-flex tw-gap-4 tw-mt-12">
                        {items.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => goTo(i)}
                                className={`tw-w-[6px] tw-h-[6px] tw-rounded-full tw-border-none tw-p-0 tw-cursor-pointer tw-transition-all tw-duration-300 ${
                                    i === activeIndex
                                        ? 'tw-bg-white tw-scale-125'
                                        : 'tw-bg-white/40 hover:tw-bg-white/70'
                                }`}
                                aria-label={intl.formatMessage(
                                    { id: 'login.content.goToSlide' },
                                    { number: i + 1 },
                                )}
                            />
                        ))}
                    </div>
                )}

                {/* Platform Updates */}
                <PlatformUpdates updates={updates} />
            </div>
        </div>
    );
};
